import DOMController from './abstract/DOMController';
import {triggerCustomEvent} from './EventTrigger';

export default class StyledSelect extends DOMController
{
    readonly NO_MATTER_VALUE = 'Не важно';

    protected group: NodeListOf<HTMLElement>;
    protected eventPrefix: '-sd';
    protected classes = {
        main: 'styled-select',
        value: 'styled-select__value',
        opened: 'styled-select_opened',
        hover: 'styled-select_hover',
        listItem: 'styled-select__item',
        listItemSelected: 'styled-select__item_selected',
        list: 'styled-select__list',
        listAbove: 'styled-select__list_above',
        multy: 'styled-select_theme_multiselect',
        toggleButton: 'styled-select__toggle-all',
        hasSelected: 'styled-select__toggle-all_has-selected',
        allSelected: 'styled-select__toggle-all_all-selected',
        noMatter: 'styled-select__no-matter',
    }

    /**
     * @param group - селектор или набор элементов
     */
    constructor(group: string|NodeListOf<HTMLElement>)
    {
        super();

        if (typeof group === 'string') {
            group = document.querySelectorAll(group);
        }
        
        this.group = group;
    }

    public init()
    {
        this.group.forEach(node => this.appendEventListeners(node));
    }

    /**
     * Выделение одного варианта списка box со значением value
     * @param box 
     * @param value 
     * @param avoidEventsTrigger 
     * @returns 
     */
    public set(box: Element, value: string, avoidEventsTrigger: boolean = false)
    {
        let input = box.querySelector(this.getInputSelector() + '[value="' + value + '"]');

        if (!input) {
            return;
        }

        // если галочка была снята, то не отправляем событие
        this.setValue(box, input.parentElement);
        this.switchDisplayedValue(box);

        if (!avoidEventsTrigger) {
            this.triggerValuesSelectedEvent(box, value);
        }
    }

    public getValues(node: Element): NodeListOf<Element>
    {
        return node.querySelectorAll('.' + this.classes.listItem);
    }

    public getSelectedDataAttribute(box: Element, attributeName: string): string[]
    {
        if (attributeName.search('data-') !== 0) {
            attributeName = 'data-' + attributeName;
        }

        let attr: string[] = [];

        box.querySelectorAll('.' + this.classes.listItemSelected + ' input').forEach(item => {
            let value = item.getAttribute(attributeName);

            if (value) {
                attr.push(value);
            }
        });

        return attr;
    }

    public isMultiselect(box: Element): boolean
    {
        return box.classList.contains(this.classes.multy);
    }

    // специальный элемент для отслеживания потери фокуса с целью закрытия выпадающего списка
    protected getFocusInput(box: HTMLElement): HTMLInputElement
    {
        return box.querySelector('input[data-input-trigger]') as HTMLInputElement;
    }

    protected appendEventListeners(box: HTMLElement)
    {
        let focusInput = this.getFocusInput(box);

        box.addEventListener('click', e => {
            e.preventDefault();

            let clickTarget = e.target as HTMLElement;
            
            // open/close popup
            if (clickTarget.classList.contains(this.classes.value)) {
                if (box.classList.contains(this.classes.opened)) {
                    this.close(box);
                } else {
                    this.open(box);
                }

                focusInput.focus();

                return;
            }

            // toggle all values
            if (clickTarget.classList.contains(this.classes.toggleButton)) {
                this.toggleAll(box);
                this.switchToggleAllState(box);
                this.switchDisplayedValue(box);
                this.triggerAllValuesSelectedEvent(box);

                focusInput.focus();

                return;
            }

            // select value in popup list
            let listItem = clickTarget.classList.contains(this.classes.listItem) ? clickTarget : clickTarget.parentElement;

            if (listItem?.classList.contains(this.classes.listItem)) {
                this.valueSelected(box, listItem);
                return;
            }
        });

        box.addEventListener('mouseenter', () => box.classList.add(this.classes.hover));
        box.addEventListener('mouseleave', () => box.classList.remove(this.classes.hover));

        focusInput.addEventListener('focusout', e => {
            if (!box.classList.contains(this.classes.hover)) {
                this.close(box);
            }
        });
    }

    protected valueSelected(box: HTMLElement, listItem: HTMLElement)
    {
        this.selectValue(box, listItem);

        this.getFocusInput(box).focus();
    }

    protected selectValue(box: HTMLElement, clickTarget: Element)
    {
        let isMultiselect = this.isMultiselect(box);

        if (!isMultiselect) {
            this.close(box);

            if (clickTarget.classList.contains(this.classes.listItemSelected)) {
                return;
            }
        }

        this.setValue(box, clickTarget);
        this.switchDisplayedValue(box);
        
        let input = clickTarget.querySelector('input') as HTMLInputElement;
        
        if (input) {
            this.triggerValuesSelectedEvent(box, input.value);
        } else if (!isMultiselect && clickTarget.classList.contains(this.classes.noMatter)) {
            this.triggerValuesSelectedEvent(box, this.NO_MATTER_VALUE);
        }

        if (isMultiselect) {
            this.switchToggleAllState(box);
        }
    }

    protected switchDisplayedValue(box: Element)
    {
        let value = box.querySelector('.' + this.classes.value) as HTMLElement;
        let selectedItems = box.querySelectorAll('.' + this.classes.listItemSelected);

        if (selectedItems.length === 0) {
            value.innerText = 'Не важно';
        } else if (selectedItems.length === 1) {
            let item = selectedItems.item(0) as HTMLElement;
            value.innerText = item.innerText;
        } else {
            value.innerText = 'Выбрано ';
            value.appendChild(this.getValueCounterElement(selectedItems.length));
        }
    }

    private getValueCounterElement(count: number): Element
    {
        let counter = document.createElement('span');

        counter.classList.add('styled-select__counter');
        counter.innerText = count.toString();

        return counter;
    }

    private setValue(box: Element, targetListItem: Element, isSelected: boolean = null)
    {
        let isMultiselect = this.isMultiselect(box);

        if (!isMultiselect && targetListItem.classList.contains(this.classes.listItemSelected)) {
            return;
        }

        if (!isMultiselect) { // снять все галочки
            box.querySelectorAll('.' + this.classes.listItem).forEach(item => {
                item.classList.remove(this.classes.listItemSelected);

                let input = item.querySelector('input');
                if (input) {
                    input.checked = false;
                }
            });
        }

        let input = targetListItem.querySelector('input') as HTMLInputElement;
        let isInputChecked = null;

        if (input === null) {
            isInputChecked = isSelected === null ? true : isSelected;
        } else {
            isInputChecked = isSelected === null ? !input.checked : isSelected;
            input.checked = isInputChecked;
        }

        if (isInputChecked) {
            targetListItem.classList.add(this.classes.listItemSelected);
        } else {
            targetListItem.classList.remove(this.classes.listItemSelected);
        }

        return isInputChecked;
    }

    private triggerAllValuesSelectedEvent(box: Element)
    {
        let values = [];

        box.querySelectorAll(this.getInputSelector() + ':checked').forEach(input => {
            values.push(input.getAttribute('value'));
        });

        triggerCustomEvent(box, 'valueselected', {
            bubbles: true,
            detail: this.createEventDetails(values, null, 'Выбрать все', values.length > 0),
        });
    }

    private triggerValuesSelectedEvent(box: Element, value: string)
    {
        let values     = [];
        let isNoMatter = value === this.NO_MATTER_VALUE;
        let isChecked  = isNoMatter; // для "Не важно" checked всегда true
        let valueTitle = isNoMatter ? this.NO_MATTER_VALUE : '';

        box.querySelectorAll(this.getInputSelector() + ':checked').forEach(input => {
            values.push(input.getAttribute('value'));
        });

        let targetInput = box.querySelector(this.getInputSelector() + '[value="' + value + '"]') as HTMLInputElement;

        if (targetInput) {
            valueTitle = targetInput.parentElement.innerText;
            isChecked  = targetInput.checked;
        }

        triggerCustomEvent(box, 'valueselected', {
            bubbles: true,
            detail: this.createEventDetails(values, value, valueTitle, isChecked)
        });
    }

    private createEventDetails(values: Array<String>, value: string|null, valueTitle: string, isChecked: boolean): object
    {
        return {
            values: values,         // все отмеченные значения на момент вызова события
            value: value,           // измененное значение
            valueTitle: valueTitle, // текстовое название значения (название для пользователя)
            isChecked: isChecked,   // true - значение value было отмечено, false - отметка была снята с value
        }
    }

    private getInputSelector(): string
    {
        return '.' + this.classes.list + ' ' + 'input[type=checkbox]';
    }

    private toggleAll(box: Element)
    {
        let uncheckedInputs = box.querySelectorAll(this.getInputSelector() + ':not(:checked)');

        if (uncheckedInputs.length > 0) {
            uncheckedInputs.forEach(input => {
                this.setChecked(input as HTMLInputElement, true);
            });
        } else {
            box.querySelectorAll(this.getInputSelector()).forEach(input => {
                this.setChecked(input as HTMLInputElement, false);
            });
        }
    }

    private switchToggleAllState(box: Element)
    {
        let toggleButton = box.querySelector('.' + this.classes.toggleButton);
        let hasChecked = false;
        let hasUnchecked = false;

        box.querySelectorAll(this.getInputSelector()).forEach(inputElement => {
            let input = inputElement as HTMLInputElement;

            hasChecked = hasChecked || input.checked;
            hasUnchecked = hasUnchecked || !input.checked;
        });

        if (hasChecked && hasUnchecked) {
            toggleButton.classList.add(this.classes.hasSelected);
            toggleButton.classList.remove(this.classes.allSelected);
        } else if (hasChecked && !hasUnchecked) {
            toggleButton.classList.add(this.classes.allSelected);
            toggleButton.classList.remove(this.classes.hasSelected);
        } else {
            toggleButton.classList.remove(this.classes.hasSelected, this.classes.allSelected);
        }
    }

    private setChecked(input: HTMLInputElement, isChecked: boolean)
    {
        let listItem = input.parentElement;

        input.checked = isChecked;
        
        if (isChecked) {
            listItem.classList.add(this.classes.listItemSelected);
        } else {
            listItem.classList.remove(this.classes.listItemSelected);
        }
    }

    protected closeAll()
    {
        this.group.forEach(item => this.close(item));
    }

    protected close(node: HTMLElement)
    {
        node.classList.remove(this.classes.opened);

        triggerCustomEvent(node, 'list-closed');
    }

    protected open(node: HTMLElement)
    {
        node.classList.add(this.classes.opened);
        this.setPopupPosition(node);
        
        triggerCustomEvent(node, 'list-opened');
    }

    protected setPopupPosition(node: HTMLElement)
    {
        let target = node.querySelector('.' + this.classes.list);
        let container = document.documentElement;
        
        if (target === null) {
            return;
        }

        target.classList.remove(this.classes.listAbove);

        let targetRect = target.getBoundingClientRect();

        if (
            targetRect.top + target.clientHeight > container.clientHeight
            && container.clientHeight - targetRect.top < targetRect.top
        ) {
            target.classList.add(this.classes.listAbove);
        }
    }
}
